/**
 * Admin surface for shop-floor kiosk terminals, operator credentials, and
 * the resource-activity mapping — three sections behind the same
 * permission.
 *
 * Terminals: the full CRUD layer behind terminals that today can only be
 * provisioned via the API. Mirrors SettingsWallboardPage's capability-token
 * pattern — provisioning and rotation each return the plaintext token
 * exactly once, and the reveal dialog after either action is the only place
 * the operator ever sees it. Closing the dialog without copying means
 * rotating again to recover access.
 *
 * Operator credentials (docs/features/shopfloor-kiosk-operator-login/):
 * enrol/reset/deactivate the 4-digit PIN each Tharstern operator badges into
 * a kiosk with — the terminal login's own credential check, added once
 * Tharstern's own `/api/operators/login` was confirmed dead. Same
 * once-only-reveal shape as the terminal token above, but a lost PIN
 * strands an operator rather than a device, so the reveal also requires an
 * explicit acknowledgement checkbox before it can be dismissed (see
 * `PinRevealDialog`).
 *
 * Resource-activity mapping (docs/features/kiosk-owned-activity-mapping/):
 * the CIS-owned machine -> Tharstern-activity mapping that overrides IDC's
 * resource-sequence config for a planned kiosk start. The list's whole
 * point is surfacing the "unconfigured" machines — the ones a planned start
 * would currently refuse — before an operator finds one mid-shift, so that
 * status renders as an unmissable banner + badge rather than a quiet table
 * cell. Mapping is explicit: an ordered activity list with exactly one
 * marked Run, edited via up/down sequence controls rather than
 * drag-and-drop (see `MapResourceActivityDialog`).
 *
 * Admin-only at v1 (Permit.SHOPFLOOR_KIOSK_MANAGE; see
 * docs/features/shopfloor-barcode-scanning/03_module_spec_shopfloor_kiosk.md
 * §9). The page is mounted inside SettingsLayout's admin-only branch in
 * `app/routes.tsx`, so role gating happens at the route layer rather than
 * here.
 */

import type { FormEvent, JSX, ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { ApiError } from "@shared-core/data/api/errors";
import type * as API from "@shared-core/data/api-types";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { extractFieldErrors } from "@shared-core/data/validation";
import { formatDateTimeOrDash } from "@shared-core/domain/formatters";

import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "../app/components/primitives/alert";
import { Badge } from "../app/components/primitives/badge";
import { Button } from "../app/components/primitives/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../app/components/primitives/card";
import { Checkbox } from "../app/components/primitives/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../app/components/primitives/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../app/components/primitives/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../app/components/primitives/alert-dialog";
import { Input } from "../app/components/primitives/input";
import { Label } from "../app/components/primitives/label";
import {
  RadioGroup,
  RadioGroupItem,
} from "../app/components/primitives/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../app/components/primitives/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  KeyRound,
  Link2,
  Pencil,
  Plus,
  Power,
  Unlink,
  UserPlus,
  X,
} from "lucide-react";

interface RevealState {
  mode: "provisioned" | "rotated";
  terminalId: string;
  token: string;
}

/** One activity picked into a mapping, in display order. Distinct from
 * `API.ResourceActivityMappingMember` — carries no `sequence_no`, since
 * order is the array's own position while the dialog is open, only
 * materialised as `sequence_no` on submit. */
interface PickedMember {
  machine_and_labour_id: number;
  code: string;
  description: string;
  is_run: boolean;
}

const RESOLUTION_LABEL: Record<string, string> = {
  mapped: "Mapped in One",
  idc_fallback: "IDC fallback",
  unconfigured: "Unconfigured — refuses",
};

const RESOLUTION_BADGE_VARIANT: Record<
  string,
  "success" | "warning" | "error"
> = {
  mapped: "success",
  idc_fallback: "warning",
  unconfigured: "error",
};

function ResolutionBadge({ resolution }: { resolution: string }): JSX.Element {
  return (
    <Badge variant={RESOLUTION_BADGE_VARIANT[resolution] ?? "secondary"}>
      {RESOLUTION_LABEL[resolution] ?? resolution}
    </Badge>
  );
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError && err.detail ? err.detail : fallback;
}

/** A machine's name alone is not a safe identifier once two machines share
 * it — Tharstern's own catalogue has duplicate names (confirmed in
 * production: resource 60 "L1 PLATEMAKING" and resource 325 "PLATEMAKING
 * XL75" both display as "Platemaking XL75"). Every user-facing string
 * that names *one specific* machine — an aria-label, a dialog title, a
 * confirmation message — appends the code the moment a collision is
 * detected in the currently-loaded candidates, computed fresh each time
 * (not hardcoded to the one pair found in production) so any future
 * collision is caught the same way; assume collisions are normal, not
 * exceptional. Machines with a unique name are left alone — most of the
 * catalogue doesn't need the extra noise. */
function machineLabel(
  candidate: { resource_name: string; resource_code: string },
  nameCollides: boolean,
): string {
  return nameCollides
    ? `${candidate.resource_name} (${candidate.resource_code})`
    : candidate.resource_name;
}

export function SettingsShopfloorKioskPage(): JSX.Element {
  const adapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();

  const [terminals, setTerminals] = useState<API.KioskTerminal[]>([]);
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");

  // Sites are loaded once at page level (they're a small, static backend
  // allowlist) and handed to both dialogs' site dropdown — see
  // CreateTerminalDialog/EditTerminalDialog. Bindable Tharstern terminals
  // are fetched inside CreateTerminalDialog itself, fresh on every open, so
  // a terminal just bound in this session immediately shows as taken.
  const [sites, setSites] = useState<string[]>([]);
  const [sitesLoading, setSitesLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<API.KioskTerminal | null>(null);
  const [deactivateTarget, setDeactivateTarget] =
    useState<API.KioskTerminal | null>(null);
  const [deactivating, setDeactivating] = useState(false);
  const [deactivateError, setDeactivateError] = useState<string | null>(null);
  const [rotatingId, setRotatingId] = useState<string | null>(null);
  const [rotateError, setRotateError] = useState<string | null>(null);

  const [reveal, setReveal] = useState<RevealState | null>(null);
  const [copied, setCopied] = useState(false);

  const loadTerminals = useCallback(async () => {
    try {
      const next = await adapter.listKioskTerminals();
      setTerminals(next);
      setPhase("ready");
    } catch {
      setPhase("error");
    }
  }, [adapter]);

  useEffect(() => {
    void loadTerminals();
  }, [loadTerminals]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await adapter.listKioskSites();
        if (!cancelled) setSites(result.sites);
      } catch {
        if (!cancelled) setSites([]);
      } finally {
        if (!cancelled) setSitesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adapter]);

  const handleProvisioned = useCallback(
    (result: API.ProvisionKioskTerminalResult) => {
      setCreateOpen(false);
      setReveal({
        mode: "provisioned",
        terminalId: result.kiosk_terminal_id,
        token: result.token,
      });
      void loadTerminals();
    },
    [loadTerminals],
  );

  const handleUpdated = useCallback((updated: API.KioskTerminal) => {
    setEditing(null);
    setTerminals((prev) =>
      prev.map((t) => (t.id === updated.id ? updated : t)),
    );
  }, []);

  const handleRotate = useCallback(
    async (terminal: API.KioskTerminal) => {
      setRotatingId(terminal.id);
      setRotateError(null);
      try {
        const result = await adapter.rotateKioskTerminalToken(terminal.id);
        setReveal({
          mode: "rotated",
          terminalId: terminal.id,
          token: result.token,
        });
        await loadTerminals();
      } catch (err) {
        setRotateError(
          errorMessage(err, "Could not rotate this terminal's token."),
        );
      } finally {
        setRotatingId(null);
      }
    },
    [adapter, loadTerminals],
  );

  const handleDeactivateConfirm = useCallback(async () => {
    if (!deactivateTarget) return;
    setDeactivating(true);
    setDeactivateError(null);
    try {
      await adapter.deactivateKioskTerminal(deactivateTarget.id);
      setDeactivateTarget(null);
      await loadTerminals();
    } catch (err) {
      setDeactivateError(
        errorMessage(err, "Could not deactivate this terminal."),
      );
    } finally {
      setDeactivating(false);
    }
  }, [adapter, deactivateTarget, loadTerminals]);

  const handleCopyToken = useCallback(async () => {
    if (!reveal) return;
    await capabilities.copyToClipboard(reveal.token);
    setCopied(true);
    // Plain setTimeout (not window.setTimeout) — keeps this file free of
    // bare browser globals, which the portability check forbids in pages.
    setTimeout(() => setCopied(false), 2000);
  }, [capabilities, reveal]);

  const handleDismissReveal = useCallback(() => {
    setReveal(null);
    setCopied(false);
  }, []);

  // ---- Operator credentials (docs/features/shopfloor-kiosk-operator-login/) —
  // a separate list/CRUD concern from the terminal admin state above, same
  // page and same permission.

  const [credentials, setCredentials] = useState<API.KioskOperatorCredential[]>(
    [],
  );
  const [credentialsPhase, setCredentialsPhase] = useState<
    "loading" | "ready" | "error"
  >("loading");
  const [enrolOpen, setEnrolOpen] = useState(false);
  const [resetTarget, setResetTarget] =
    useState<API.KioskOperatorCredential | null>(null);
  const [deactivateCredentialTarget, setDeactivateCredentialTarget] =
    useState<API.KioskOperatorCredential | null>(null);
  const [deactivatingCredential, setDeactivatingCredential] = useState(false);
  const [deactivateCredentialError, setDeactivateCredentialError] = useState<
    string | null
  >(null);
  const [credentialReveal, setCredentialReveal] =
    useState<API.EnrolOrResetOperatorCredentialResult | null>(null);

  const loadCredentials = useCallback(async () => {
    try {
      const next = await adapter.listOperatorCredentials();
      setCredentials(next);
      setCredentialsPhase("ready");
    } catch {
      setCredentialsPhase("error");
    }
  }, [adapter]);

  useEffect(() => {
    void loadCredentials();
  }, [loadCredentials]);

  const handleEnrolledOrReset = useCallback(
    (result: API.EnrolOrResetOperatorCredentialResult) => {
      setEnrolOpen(false);
      setResetTarget(null);
      setCredentialReveal(result);
      void loadCredentials();
    },
    [loadCredentials],
  );

  const handleDeactivateCredentialConfirm = useCallback(async () => {
    if (!deactivateCredentialTarget) return;
    setDeactivatingCredential(true);
    setDeactivateCredentialError(null);
    try {
      await adapter.deactivateOperatorCredential(
        deactivateCredentialTarget.tharstern_operator_id,
      );
      setDeactivateCredentialTarget(null);
      await loadCredentials();
    } catch (err) {
      setDeactivateCredentialError(
        errorMessage(err, "Could not deactivate this operator's credential."),
      );
    } finally {
      setDeactivatingCredential(false);
    }
  }, [adapter, deactivateCredentialTarget, loadCredentials]);

  // ---- Resource-activity mapping (docs/features/kiosk-owned-activity-mapping/) —
  // a third, separate concern from the two above: which Tharstern activity
  // id(s) a machine books a planned kiosk start against.

  const [candidates, setCandidates] = useState<API.ResourceActivityCandidate[]>(
    [],
  );
  const [candidatesPhase, setCandidatesPhase] = useState<
    "loading" | "ready" | "error"
  >("loading");
  // `mapping: null` -> creating a new (possibly first, possibly an
  // alternative) mapping for `candidate`; `mapping` set -> editing that
  // specific mapping in place. One dialog, two modes — see
  // MapResourceActivityDialog.
  const [mapDialogState, setMapDialogState] = useState<{
    candidate: API.ResourceActivityCandidate;
    mapping: API.ResourceActivityMapping | null;
  } | null>(null);
  const [unmapTarget, setUnmapTarget] = useState<{
    candidate: API.ResourceActivityCandidate;
    mapping: API.ResourceActivityMapping;
  } | null>(null);
  const [unmapping, setUnmapping] = useState(false);
  const [unmapError, setUnmapError] = useState<string | null>(null);

  const loadCandidates = useCallback(async () => {
    try {
      const next = await adapter.listResourceActivityCandidates();
      setCandidates(next);
      setCandidatesPhase("ready");
    } catch {
      setCandidatesPhase("error");
    }
  }, [adapter]);

  useEffect(() => {
    void loadCandidates();
  }, [loadCandidates]);

  const handleMapped = useCallback(() => {
    setMapDialogState(null);
    void loadCandidates();
  }, [loadCandidates]);

  const handleUnmapConfirm = useCallback(async () => {
    if (!unmapTarget) return;
    setUnmapping(true);
    setUnmapError(null);
    try {
      await adapter.unmapResourceActivity(unmapTarget.mapping.id);
      setUnmapTarget(null);
      await loadCandidates();
    } catch (err) {
      setUnmapError(errorMessage(err, "Could not unmap this mapping."));
    } finally {
      setUnmapping(false);
    }
  }, [adapter, unmapTarget, loadCandidates]);

  // "In use" = has appeared on a job operation planned to start in the
  // last 90 days (see list_resource_activity_candidates.IN_USE_LOOKBACK_
  // DAYS on the backend) — distinguishes the handful of machines that
  // actually block a planned start from the rest of Tharstern's ~148-row
  // machine catalogue, most of which have never run a job at all. Sorting
  // in-use machines first — not just badging them — is what makes "lead
  // with the ones that matter" real: a reader scanning top-down meets the
  // urgent rows before the background noise, regardless of table length.
  const sortedCandidates = [...candidates].sort(
    (a, b) => Number(b.is_in_use) - Number(a.is_in_use),
  );
  const unconfigured = candidates.filter(
    (c) => c.resolution === "unconfigured",
  );
  const unconfiguredInUseCount = unconfigured.filter((c) => c.is_in_use).length;
  const unconfiguredIdleCount = unconfigured.length - unconfiguredInUseCount;

  // Tharstern's machine names collide — confirmed in production (resource
  // 60 "L1 PLATEMAKING" and resource 325 "PLATEMAKING XL75" both display
  // as "Platemaking XL75"), and per the investigation this is normal, not
  // exceptional (three separate naming collisions found in one day). The
  // code is what actually distinguishes them, so a duplicated name needs
  // its code promoted from quiet subtext to something a skimming reader
  // can't miss.
  const duplicateResourceNames = new Set(
    Object.entries(
      candidates.reduce<Record<string, number>>((counts, c) => {
        const key = c.resource_name.trim().toLowerCase();
        counts[key] = (counts[key] ?? 0) + 1;
        return counts;
      }, {}),
    )
      .filter(([, count]) => count > 1)
      .map(([name]) => name),
  );
  const isDuplicateName = (name: string): boolean =>
    duplicateResourceNames.has(name.trim().toLowerCase());

  return (
    <PageShell onProvision={() => setCreateOpen(true)}>
      {/* Terminals and operator credentials are both small, fixed-size
       * concerns (bounded by the terminal/operator estate, not by data
       * growth) — they share a row on wide viewports instead of each
       * claiming the full page width above the mapping table, which is
       * the one section that actually grows with the estate and needs
       * the room. Single column below `lg` so a narrow window still
       * gets one readable column per card. */}
      <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
        <Card>
          <CardHeader>
            <CardTitle>Provisioned terminals</CardTitle>
            <CardDescription>
              Every kiosk terminal ever bound to a Tharstern terminal, across
              every site. Deactivating keeps the row for audit — it never
              hard-deletes.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {phase === "loading" && (
              <p className="text-muted-foreground">Loading…</p>
            )}
            {phase === "error" && (
              <p className="text-destructive">
                Could not load kiosk terminals. Please refresh and try again.
              </p>
            )}
            {phase === "ready" && terminals.length === 0 && (
              <p className="text-muted-foreground">
                No kiosk terminals have been provisioned yet.
              </p>
            )}
            {phase === "ready" && terminals.length > 0 && (
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Site</TableHead>
                      <TableHead>Display name</TableHead>
                      <TableHead>Tharstern terminal id</TableHead>
                      <TableHead>Token</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Last used</TableHead>
                      <TableHead className="w-32">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {terminals.map((terminal) => (
                      <TableRow key={terminal.id}>
                        <TableCell>{terminal.site}</TableCell>
                        <TableCell className="font-medium">
                          {terminal.display_name}
                        </TableCell>
                        <TableCell className="tabular-nums">
                          {terminal.tharstern_terminal_id}
                        </TableCell>
                        <TableCell className="tabular-nums">
                          …{terminal.token_suffix}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              terminal.is_active ? "default" : "secondary"
                            }
                          >
                            {terminal.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {formatTimestamp(terminal.last_used_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditing(terminal)}
                              aria-label={`Edit ${terminal.display_name}`}
                            >
                              <Pencil className="size-4" />
                            </Button>
                            {terminal.is_active && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRotate(terminal)}
                                disabled={rotatingId === terminal.id}
                                aria-label={`Rotate token for ${terminal.display_name}`}
                              >
                                <KeyRound className="size-4" />
                              </Button>
                            )}
                            {terminal.is_active && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeactivateTarget(terminal)}
                                aria-label={`Deactivate ${terminal.display_name}`}
                              >
                                <Power className="size-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            {rotateError && (
              <p role="alert" className="mt-3 text-sm text-destructive">
                {rotateError}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-4">
            <div>
              <CardTitle>Operator credentials</CardTitle>
              <CardDescription>
                Tharstern operators enrolled to badge into a kiosk with a PIN.
                Deactivating keeps the row for audit — enrolling again issues a
                fresh PIN.
              </CardDescription>
            </div>
            <Button
              type="button"
              onClick={() => setEnrolOpen(true)}
              data-testid="credential-enrol-button"
            >
              <UserPlus className="size-4" />
              Enrol operator
            </Button>
          </CardHeader>
          <CardContent>
            {credentialsPhase === "loading" && (
              <p className="text-muted-foreground">Loading…</p>
            )}
            {credentialsPhase === "error" && (
              <p className="text-destructive">
                Could not load operator credentials. Please refresh and try
                again.
              </p>
            )}
            {credentialsPhase === "ready" && credentials.length === 0 && (
              <p className="text-muted-foreground">
                No operators are enrolled yet.
              </p>
            )}
            {credentialsPhase === "ready" && credentials.length > 0 && (
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tharstern operator id</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>PIN set</TableHead>
                      <TableHead className="w-32">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {credentials.map((credential) => (
                      <TableRow key={credential.tharstern_operator_id}>
                        <TableCell className="tabular-nums">
                          {credential.tharstern_operator_id}
                        </TableCell>
                        <TableCell className="font-medium">
                          {credential.operator_display_name}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              credential.is_active ? "default" : "secondary"
                            }
                          >
                            {credential.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {formatTimestamp(credential.pin_set_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setResetTarget(credential)}
                              aria-label={`Reset PIN for ${credential.operator_display_name}`}
                            >
                              <KeyRound className="size-4" />
                            </Button>
                            {credential.is_active && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  setDeactivateCredentialTarget(credential)
                                }
                                aria-label={`Deactivate credential for ${credential.operator_display_name}`}
                              >
                                <Power className="size-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Resource → activity mapping</CardTitle>
          <CardDescription>
            Which Tharstern activity a planned kiosk start books against, per
            machine. A mapping in One overrides Tharstern's IDC configuration
            where both exist; unmapping reverts to IDC, or to refusal if the
            machine has no IDC sequence either.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {candidatesPhase === "loading" && (
            <p className="text-muted-foreground">Loading…</p>
          )}
          {candidatesPhase === "error" && (
            <p className="text-destructive">
              Could not load machines. Please refresh and try again.
            </p>
          )}
          {candidatesPhase === "ready" && candidates.length === 0 && (
            <p className="text-muted-foreground">
              No machines have appeared on a Tharstern job operation recently.
            </p>
          )}
          {candidatesPhase === "ready" && candidates.length > 0 && (
            <div className="space-y-4">
              {/* Lead with the machines that actually block work — ones
               * that have appeared on a Tharstern job operation in the last 90 days
               * — rather than every unconfigured row in Tharstern's whole
               * ~148-machine catalogue. The idle count still shows, but
               * de-emphasised below, since "148 machines have no activity
               * configured" (when only ~11 ever run a job) buries the
               * ones that matter and trains the reader to ignore the
               * banner. See docs/features/kiosk-owned-activity-mapping/. */}
              {unconfiguredInUseCount > 0 && (
                <Alert variant="destructive" data-testid="unconfigured-banner">
                  <AlertTriangle />
                  <AlertTitle>
                    {unconfiguredInUseCount === 1
                      ? "1 machine in use has no activity configured"
                      : `${unconfiguredInUseCount} machines in use have no activity configured`}
                  </AlertTitle>
                  <AlertDescription>
                    A planned start on{" "}
                    {unconfiguredInUseCount === 1 ? "it" : "these"} will be
                    refused until it is mapped here, or given an IDC resource
                    sequence. "In use" means it has appeared on a Tharstern job
                    operation in the last 90 days.
                  </AlertDescription>
                </Alert>
              )}
              {unconfiguredIdleCount > 0 && (
                <p
                  className="text-sm text-muted-foreground"
                  data-testid="unconfigured-idle-note"
                >
                  {unconfiguredIdleCount === 1
                    ? "1 more machine has no activity configured"
                    : `${unconfiguredIdleCount} more machines have no activity configured`}
                  , but {unconfiguredIdleCount === 1 ? "hasn't" : "haven't"}{" "}
                  appeared on a Tharstern job operation in the last 90 days.
                </p>
              )}
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Machine</TableHead>
                      <TableHead>Planned start</TableHead>
                      <TableHead>Mapped activities</TableHead>
                      <TableHead className="w-36">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedCandidates.map((candidate) => (
                      <TableRow
                        key={candidate.tharstern_resource_id}
                        data-testid={`resource-row-${candidate.resource_code}`}
                        className={
                          candidate.is_in_use ? undefined : "opacity-60"
                        }
                      >
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium">
                              {candidate.resource_name}
                            </span>
                            {isDuplicateName(candidate.resource_name) && (
                              <Badge
                                variant="outline"
                                data-testid={`duplicate-name-badge-${candidate.resource_code}`}
                              >
                                Shares this name — {candidate.resource_code}
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {candidate.resource_code}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <ResolutionBadge
                              resolution={candidate.resolution}
                            />
                            {!candidate.is_in_use && (
                              <span
                                className="text-xs text-muted-foreground"
                                data-testid={`not-in-use-note-${candidate.resource_code}`}
                              >
                                Not used in the last 90 days
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="whitespace-normal align-top">
                          {candidate.mappings.length === 0 ? (
                            <span className="text-muted-foreground">—</span>
                          ) : (
                            <div className="space-y-2">
                              {/* The difference between a silent start and an
                               * operator tap — make several mappings
                               * unmissable, not just a longer list. */}
                              {candidate.mappings.length > 1 && (
                                <Badge
                                  variant="outline"
                                  data-testid={`several-mappings-badge-${candidate.resource_code}`}
                                >
                                  {candidate.mappings.length} alternatives —
                                  operator chooses
                                </Badge>
                              )}
                              {/* Capped width + wrap (not the table's default
                               * whitespace-nowrap) so a wide card — a long
                               * activity description, several members —
                               * grows taller, not wider. Left unbounded, a
                               * card this wide inherits nowrap from the cell
                               * and pushes the whole row past the Actions
                               * column instead — see 98_known_issues.md. */}
                              {candidate.mappings.map((mapping) => (
                                <div
                                  key={mapping.id}
                                  className="max-w-md rounded-md border p-2"
                                  data-testid={`mapping-row-${mapping.id}`}
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <span className="text-sm font-medium break-words">
                                      {mapping.name}
                                    </span>
                                    <div className="flex shrink-0 gap-1">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() =>
                                          setMapDialogState({
                                            candidate,
                                            mapping,
                                          })
                                        }
                                        aria-label={`Edit ${mapping.name} for ${machineLabel(candidate, isDuplicateName(candidate.resource_name))}`}
                                      >
                                        <Pencil className="size-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() =>
                                          setUnmapTarget({ candidate, mapping })
                                        }
                                        aria-label={`Unmap ${mapping.name} for ${machineLabel(candidate, isDuplicateName(candidate.resource_name))}`}
                                      >
                                        <Unlink className="size-4 text-destructive" />
                                      </Button>
                                    </div>
                                  </div>
                                  <ol className="mt-1 space-y-0.5 text-sm">
                                    {mapping.members.map((member) => (
                                      <li
                                        key={member.machine_and_labour_id}
                                        className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5"
                                      >
                                        <span className="text-muted-foreground tabular-nums">
                                          {member.sequence_no}.
                                        </span>
                                        <span className="break-words">
                                          {member.code}
                                        </span>
                                        {member.is_run && (
                                          <Badge variant="outline">Run</Badge>
                                        )}
                                      </li>
                                    ))}
                                  </ol>
                                </div>
                              ))}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              setMapDialogState({ candidate, mapping: null })
                            }
                            aria-label={
                              candidate.mappings.length === 0
                                ? `Map ${machineLabel(candidate, isDuplicateName(candidate.resource_name))}`
                                : `Add an alternative mapping for ${machineLabel(candidate, isDuplicateName(candidate.resource_name))}`
                            }
                          >
                            <Link2 className="size-4" />
                            {candidate.mappings.length === 0
                              ? "Map"
                              : "Add alternative"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <CreateTerminalDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onProvisioned={handleProvisioned}
        sites={sites}
        sitesLoading={sitesLoading}
      />

      {editing && (
        <EditTerminalDialog
          terminal={editing}
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
          onUpdated={handleUpdated}
          sites={sites}
          sitesLoading={sitesLoading}
        />
      )}

      <AlertDialog
        open={!!deactivateTarget}
        onOpenChange={(open) => {
          if (!open) {
            setDeactivateTarget(null);
            setDeactivateError(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate this terminal?</AlertDialogTitle>
            <AlertDialogDescription>
              {deactivateTarget
                ? `"${deactivateTarget.display_name}" will stop being able to badge in or scan immediately. This is a soft-delete — the row stays for audit, and provisioning a replacement needs a new terminal.`
                : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deactivateError && (
            <p role="alert" className="text-sm text-destructive">
              {deactivateError}
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deactivating}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeactivateConfirm}
              disabled={deactivating}
            >
              {deactivating ? "Deactivating…" : "Deactivate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {reveal && (
        <TokenRevealDialog
          reveal={reveal}
          copied={copied}
          onCopy={handleCopyToken}
          onDismiss={handleDismissReveal}
        />
      )}

      <EnrolOrResetOperatorDialog
        open={enrolOpen || !!resetTarget}
        target={resetTarget}
        onOpenChange={(open) => {
          if (!open) {
            setEnrolOpen(false);
            setResetTarget(null);
          }
        }}
        onSuccess={handleEnrolledOrReset}
      />

      <AlertDialog
        open={!!deactivateCredentialTarget}
        onOpenChange={(open) => {
          if (!open) {
            setDeactivateCredentialTarget(null);
            setDeactivateCredentialError(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate this operator's PIN?</AlertDialogTitle>
            <AlertDialogDescription>
              {deactivateCredentialTarget
                ? `"${deactivateCredentialTarget.operator_display_name}" will no longer be able to badge into any kiosk with this PIN or a badge encoding it. This is a soft-delete — the row stays for audit; re-enrolling issues a fresh PIN and requires printing a new badge.`
                : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deactivateCredentialError && (
            <p role="alert" className="text-sm text-destructive">
              {deactivateCredentialError}
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deactivatingCredential}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeactivateCredentialConfirm}
              disabled={deactivatingCredential}
            >
              {deactivatingCredential ? "Deactivating…" : "Deactivate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {credentialReveal && (
        <PinRevealDialog
          reveal={credentialReveal}
          onDismiss={() => setCredentialReveal(null)}
        />
      )}

      {mapDialogState && (
        <MapResourceActivityDialog
          candidate={mapDialogState.candidate}
          mapping={mapDialogState.mapping}
          nameCollides={isDuplicateName(mapDialogState.candidate.resource_name)}
          onOpenChange={(open) => {
            if (!open) setMapDialogState(null);
          }}
          onMapped={handleMapped}
        />
      )}

      <AlertDialog
        open={!!unmapTarget}
        onOpenChange={(open) => {
          if (!open) {
            setUnmapTarget(null);
            setUnmapError(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unmap this mapping?</AlertDialogTitle>
            <AlertDialogDescription>
              {unmapTarget
                ? unmapTarget.candidate.mappings.length > 1
                  ? `"${unmapTarget.mapping.name}" will no longer be offered for "${machineLabel(unmapTarget.candidate, isDuplicateName(unmapTarget.candidate.resource_name))}" — its other mapping${unmapTarget.candidate.mappings.length > 2 ? "s stay" : " stays"} active. The mapping row stays for audit.`
                  : `"${machineLabel(unmapTarget.candidate, isDuplicateName(unmapTarget.candidate.resource_name))}" will revert to ${
                      unmapTarget.candidate.has_idc_fallback
                        ? "its IDC resource-sequence configuration"
                        : "refusing a planned start — it has no IDC fallback either"
                    }. The mapping row stays for audit; mapping again adds a new alternative.`
                : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {unmapError && (
            <p role="alert" className="text-sm text-destructive">
              {unmapError}
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={unmapping}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUnmapConfirm}
              disabled={unmapping}
            >
              {unmapping ? "Unmapping…" : "Unmap"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageShell>
  );
}

// ---------------------------------------------------------------- pieces ---

function PageShell({
  children,
  onProvision,
}: {
  children: ReactNode;
  onProvision: () => void;
}): JSX.Element {
  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Kiosk terminals</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Provision, re-site, and manage the access tokens for shop-floor
            kiosk terminals.
          </p>
        </div>
        <Button
          type="button"
          onClick={onProvision}
          data-testid="kiosk-provision-button"
        >
          <Plus className="size-4" />
          Provision terminal
        </Button>
      </header>
      {children}
    </div>
  );
}

function CreateTerminalDialog({
  open,
  onOpenChange,
  onProvisioned,
  sites,
  sitesLoading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProvisioned: (result: API.ProvisionKioskTerminalResult) => void;
  sites: string[];
  sitesLoading: boolean;
}): JSX.Element {
  const adapter = useDataAdapter();
  const [terminals, setTerminals] = useState<API.BindableTharsternTerminal[]>(
    [],
  );
  const [terminalsPhase, setTerminalsPhase] = useState<
    "loading" | "ready" | "error"
  >("loading");
  const [tharsternTerminalId, setTharsternTerminalId] = useState("");
  const [site, setSite] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadTerminals = useCallback(async () => {
    setTerminalsPhase("loading");
    try {
      const next = await adapter.listBindableTharsternTerminals();
      setTerminals(next);
      setTerminalsPhase("ready");
    } catch {
      setTerminals([]);
      setTerminalsPhase("error");
    }
  }, [adapter]);

  // Fetched fresh on every open (not once on mount) so a terminal bound in
  // an earlier session — or by this dialog a moment ago — shows up as
  // taken the next time the admin opens it.
  useEffect(() => {
    if (open) void loadTerminals();
  }, [open, loadTerminals]);

  function reset(): void {
    setTharsternTerminalId("");
    setSite("");
    setDisplayName("");
    setError(null);
  }

  function handleSelectTerminal(value: string): void {
    setTharsternTerminalId(value);
    const picked = terminals.find(
      (t) => String(t.tharstern_terminal_id) === value,
    );
    // Always overwrite on selection — editable afterwards, but re-picking
    // (even the same terminal) resets the prefill rather than trying to
    // detect whether the admin had manually edited it since.
    if (picked) setDisplayName(picked.name);
  }

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);

    if (!tharsternTerminalId) {
      setError("Select a Tharstern terminal to bind.");
      return;
    }
    if (!site) {
      setError("Site is required.");
      return;
    }
    if (!displayName.trim()) {
      setError("Display name is required.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await adapter.provisionKioskTerminal({
        tharstern_terminal_id: Number(tharsternTerminalId),
        site,
        display_name: displayName.trim(),
      });
      reset();
      onProvisioned(result);
    } catch (err) {
      setError(
        errorMessage(
          err,
          "Could not provision this terminal. Please try again.",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  }

  const noBindableTerminals =
    terminalsPhase === "ready" && terminals.length === 0;
  const allBound =
    terminalsPhase === "ready" &&
    terminals.length > 0 &&
    terminals.every((t) => t.is_bound);
  const noSitesAvailable = !sitesLoading && sites.length === 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Provision a kiosk terminal</DialogTitle>
          <DialogDescription>
            Binds a new kiosk to a real Tharstern terminal and mints its access
            token. The token is shown exactly once, immediately after.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="kiosk-tharstern-id">Tharstern terminal</Label>
            {terminalsPhase === "loading" && (
              <p className="text-sm text-muted-foreground">
                Loading terminals…
              </p>
            )}
            {terminalsPhase === "error" && (
              <p className="text-sm text-destructive">
                Could not load Tharstern terminals. Close and reopen this dialog
                to try again.
              </p>
            )}
            {terminalsPhase === "ready" && noBindableTerminals && (
              <p className="text-sm text-destructive">
                No Tharstern terminals are available to bind.
              </p>
            )}
            {terminalsPhase === "ready" && terminals.length > 0 && (
              <>
                <Select
                  value={tharsternTerminalId}
                  onValueChange={handleSelectTerminal}
                >
                  <SelectTrigger id="kiosk-tharstern-id" aria-invalid={!!error}>
                    <SelectValue placeholder="Select a terminal" />
                  </SelectTrigger>
                  <SelectContent>
                    {terminals.map((t) => (
                      <SelectItem
                        key={t.tharstern_terminal_id}
                        value={String(t.tharstern_terminal_id)}
                        disabled={t.is_bound}
                      >
                        {t.is_bound
                          ? `${t.name} — Assigned to ${t.assigned_display_name}`
                          : t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {allBound && (
                  <p className="text-sm text-muted-foreground">
                    Every Tharstern terminal is already bound to a live kiosk.
                  </p>
                )}
              </>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="kiosk-site">Site</Label>
            {sitesLoading && (
              <p className="text-sm text-muted-foreground">Loading sites…</p>
            )}
            {noSitesAvailable && (
              <p className="text-sm text-destructive">
                No sites are available. Please refresh and try again.
              </p>
            )}
            {!sitesLoading && sites.length > 0 && (
              <Select value={site} onValueChange={setSite}>
                <SelectTrigger id="kiosk-site" aria-invalid={!!error}>
                  <SelectValue placeholder="Select a site" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="kiosk-display-name">Display name</Label>
            <Input
              id="kiosk-display-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Litho 3 — BU"
              aria-invalid={!!error}
              aria-describedby={error ? "kiosk-provision-error" : undefined}
            />
          </div>
          {error && (
            <p
              id="kiosk-provision-error"
              role="alert"
              className="text-sm text-destructive"
            >
              {error}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Provisioning…" : "Provision"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditTerminalDialog({
  terminal,
  onOpenChange,
  onUpdated,
  sites,
  sitesLoading,
}: {
  terminal: API.KioskTerminal;
  onOpenChange: (open: boolean) => void;
  onUpdated: (updated: API.KioskTerminal) => void;
  sites: string[];
  sitesLoading: boolean;
}): JSX.Element {
  const adapter = useDataAdapter();
  const [site, setSite] = useState(terminal.site);
  const [displayName, setDisplayName] = useState(terminal.display_name);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const noSitesAvailable = !sitesLoading && sites.length === 0;

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);

    if (!site) {
      setError("Site is required.");
      return;
    }
    if (!displayName.trim()) {
      setError("Display name is required.");
      return;
    }

    setSubmitting(true);
    try {
      const updated = await adapter.updateKioskTerminal(terminal.id, {
        site,
        display_name: displayName.trim(),
      });
      onUpdated(updated);
    } catch (err) {
      setError(
        errorMessage(err, "Could not save these changes. Please try again."),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit "{terminal.display_name}"</DialogTitle>
          <DialogDescription>
            Update the site and display name. This does not touch the terminal's
            access token.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="kiosk-edit-site">Site</Label>
            {sitesLoading && (
              <p className="text-sm text-muted-foreground">Loading sites…</p>
            )}
            {noSitesAvailable && (
              <p className="text-sm text-destructive">
                No sites are available. Please refresh and try again.
              </p>
            )}
            {!sitesLoading && sites.length > 0 && (
              <Select value={site} onValueChange={setSite}>
                <SelectTrigger id="kiosk-edit-site" aria-invalid={!!error}>
                  <SelectValue placeholder="Select a site" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="kiosk-edit-display-name">Display name</Label>
            <Input
              id="kiosk-edit-display-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              aria-invalid={!!error}
              aria-describedby={error ? "kiosk-edit-error" : undefined}
            />
          </div>
          {error && (
            <p
              id="kiosk-edit-error"
              role="alert"
              className="text-sm text-destructive"
            >
              {error}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TokenRevealDialog({
  reveal,
  copied,
  onCopy,
  onDismiss,
}: {
  reveal: RevealState;
  copied: boolean;
  onCopy: () => void;
  onDismiss: () => void;
}): JSX.Element {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kiosk-token-reveal-title"
    >
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle id="kiosk-token-reveal-title">
            {reveal.mode === "provisioned"
              ? "Terminal provisioned — copy the token now"
              : "New token — copy now"}
          </CardTitle>
          <CardDescription>
            This is the only time the plaintext token is shown. Enter it on the
            terminal's own kiosk-login screen to connect it.
            {reveal.mode === "rotated" &&
              " The previous token has already been invalidated."}{" "}
            Closing this dialog without copying means rotating again to recover
            access.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <div className="flex items-baseline justify-between">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">
                Terminal token
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={onCopy}
                data-testid="kiosk-copy-token"
              >
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>
            <code className="mt-2 block break-all rounded-md bg-muted px-3 py-2 text-sm">
              {reveal.token}
            </code>
          </div>
          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={onDismiss}
              data-testid="kiosk-reveal-dismiss"
            >
              I've saved the token
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ------------------------------------------------- Operator credentials ---

function EnrolOrResetOperatorDialog({
  open,
  target,
  onOpenChange,
  onSuccess,
}: {
  open: boolean;
  /** `null` — enrolling a brand-new operator id (typed below).
   * Otherwise — resetting this already-enrolled operator's PIN; the id is
   * fixed and shown read-only. Same endpoint either way
   * (`enrolOrResetOperatorCredential`) — the backend enrols or resets
   * depending on whether a row already exists for the id. */
  target: API.KioskOperatorCredential | null;
  onOpenChange: (open: boolean) => void;
  onSuccess: (result: API.EnrolOrResetOperatorCredentialResult) => void;
}): JSX.Element {
  const adapter = useDataAdapter();
  const [operatorId, setOperatorId] = useState("");
  const [pinMode, setPinMode] = useState<"generate" | "manual">("generate");
  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pinError, setPinError] = useState<string | null>(null);

  function reset(): void {
    setOperatorId("");
    setPinMode("generate");
    setPin("");
    setError(null);
    setPinError(null);
  }

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setPinError(null);

    const tharsternOperatorId = target
      ? target.tharstern_operator_id
      : Number(operatorId);
    if (
      !target &&
      (!Number.isInteger(tharsternOperatorId) || tharsternOperatorId <= 0)
    ) {
      setError("Enter a valid Tharstern operator id.");
      return;
    }
    if (pinMode === "manual" && !/^\d{4}$/.test(pin)) {
      setPinError("Enter a 4-digit PIN.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await adapter.enrolOrResetOperatorCredential(
        tharsternOperatorId,
        pinMode === "manual" ? pin : undefined,
      );
      reset();
      onSuccess(result);
    } catch (err) {
      // pin_too_weak is a field-tagged validation error
      // (.claude/rules/api-validation.md) — bind it to the PIN input,
      // never a bare banner.
      const fieldErrors = extractFieldErrors(err);
      const weakPin = fieldErrors.find((f) => f.field === "pin");
      if (weakPin) {
        setPinError(weakPin.message);
      } else {
        setError(errorMessage(err, "Could not enrol this operator."));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {target
              ? `Reset PIN for "${target.operator_display_name}"`
              : "Enrol an operator"}
          </DialogTitle>
          <DialogDescription>
            {target
              ? "Issues a fresh PIN — the old one (and any badge encoding it) stops working immediately. The new PIN is shown exactly once, next."
              : "Binds a kiosk PIN to a Tharstern operator id. The PIN is shown exactly once, immediately after."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="credential-operator-id">
              Tharstern operator id
            </Label>
            {target ? (
              <p className="text-sm">
                {target.tharstern_operator_id} — {target.operator_display_name}
              </p>
            ) : (
              <Input
                id="credential-operator-id"
                type="number"
                inputMode="numeric"
                value={operatorId}
                onChange={(e) => setOperatorId(e.target.value)}
                placeholder="e.g. 4821"
                aria-invalid={!!error}
                aria-describedby={error ? "credential-enrol-error" : undefined}
              />
            )}
          </div>

          <div className="space-y-2">
            <Label>PIN</Label>
            <RadioGroup
              value={pinMode}
              onValueChange={(v) => setPinMode(v as "generate" | "manual")}
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="generate" id="credential-pin-generate" />
                <Label
                  htmlFor="credential-pin-generate"
                  className="font-normal"
                >
                  Generate one for me
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="manual" id="credential-pin-manual" />
                <Label htmlFor="credential-pin-manual" className="font-normal">
                  Choose a 4-digit PIN
                </Label>
              </div>
            </RadioGroup>
            {pinMode === "manual" && (
              <Input
                id="credential-pin-value"
                type="password"
                inputMode="numeric"
                maxLength={4}
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="4-digit PIN"
                aria-label="4-digit PIN"
                aria-invalid={!!pinError}
                aria-describedby={pinError ? "credential-pin-error" : undefined}
              />
            )}
            {pinError && (
              <p
                id="credential-pin-error"
                role="alert"
                className="text-sm text-destructive"
              >
                {pinError}
              </p>
            )}
          </div>

          {error && (
            <p
              id="credential-enrol-error"
              role="alert"
              className="text-sm text-destructive"
            >
              {error}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving…" : target ? "Reset PIN" : "Enrol"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The PIN's once-only reveal. Unlike TokenRevealDialog's plain "I've saved
 * it" dismiss, a lost PIN is worse than a lost terminal token — it strands
 * an operator, not a device — so dismissal requires an explicit
 * acknowledgement checkbox, not just a button label. Mirrors
 * TokenRevealDialog's hand-rolled overlay (not the Dialog primitive) so
 * there's no accidental Escape-to-close.
 *
 * Also shows the badge's barcode payload (`{operator_id}:{pin}`, the format
 * kioskBadge.ts parses) alongside the bare PIN, with its own copy button —
 * this is the only moment either value exists in plaintext, and whoever
 * prints the physical badge needs the combined string, not just the PIN.
 */
function PinRevealDialog({
  reveal,
  onDismiss,
}: {
  reveal: API.EnrolOrResetOperatorCredentialResult;
  onDismiss: () => void;
}): JSX.Element {
  const capabilities = usePlatformCapabilities();
  const [copied, setCopied] = useState(false);
  const [badgePayloadCopied, setBadgePayloadCopied] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  // The format kioskBadge.ts parses on the terminal (docs/features/
  // shopfloor-kiosk-operator-login/ decision 3 + 99_outstanding_setup.md
  // §2: "Badges encode {operator_id}:{pin} as a scannable barcode"). Whoever
  // prints the badge needs this exact string, not the bare PIN above — and
  // this reveal is the only moment it can be assembled, since the PIN is
  // never shown again after this dialog closes.
  const badgePayload = `${reveal.tharstern_operator_id}:${reveal.pin}`;

  async function handleCopy(): Promise<void> {
    await capabilities.copyToClipboard(reveal.pin);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function handleCopyBadgePayload(): Promise<void> {
    await capabilities.copyToClipboard(badgePayload);
    setBadgePayloadCopied(true);
    setTimeout(() => setBadgePayloadCopied(false), 2000);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="credential-pin-reveal-title"
    >
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle id="credential-pin-reveal-title">
            PIN set for {reveal.operator_display_name}
          </CardTitle>
          <CardDescription>
            This PIN will not be shown again — write it onto{" "}
            {reveal.operator_display_name}'s badge now. There is no "show me
            this operator's PIN" screen: a lost badge means resetting the PIN
            and printing a new one.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <div className="flex items-baseline justify-between">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">
                Operator PIN
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleCopy}
                data-testid="credential-copy-pin"
              >
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>
            <code className="mt-2 block rounded-md bg-muted px-3 py-4 text-center text-4xl font-semibold tracking-[0.5em]">
              {reveal.pin}
            </code>
          </div>
          <div>
            <div className="flex items-baseline justify-between">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">
                Badge barcode payload
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleCopyBadgePayload}
                data-testid="credential-copy-badge-payload"
              >
                {badgePayloadCopied ? "Copied" : "Copy"}
              </Button>
            </div>
            <code
              className="mt-2 block break-all rounded-md bg-muted px-3 py-2 text-sm"
              data-testid="credential-badge-payload"
            >
              {badgePayload}
            </code>
            <p className="mt-1 text-xs text-muted-foreground">
              Encode this exact string as the badge's scannable barcode — it's
              what the terminal reads back, not the plain PIN above.
            </p>
          </div>
          <Label
            htmlFor="credential-reveal-acknowledge"
            className="flex items-start gap-2 font-normal"
          >
            <Checkbox
              id="credential-reveal-acknowledge"
              checked={acknowledged}
              onCheckedChange={(v) => setAcknowledged(v === true)}
              className="mt-0.5"
            />
            <span>
              I've written this PIN and barcode payload onto the operator's
              badge. Neither will be shown again.
            </span>
          </Label>
          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              disabled={!acknowledged}
              onClick={onDismiss}
              data-testid="credential-reveal-dismiss"
            >
              Done — I've saved it
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// --------------------------------------------- Resource-activity mapping ---

/**
 * Map a new mapping, or edit an existing one, for one machine: name it,
 * pick activities in order, mark exactly one Run. `mapping: null` creates
 * (the machine's first mapping, or an additional alternative — several may
 * coexist per machine, see the module update spec's several-named-mappings
 * revision); `mapping` set edits that one mapping's name/members in place.
 *
 * The search box defaults to `candidate.resource_code` candidates and
 * switches to a full-catalogue search the moment the admin types — never
 * scoped-only, so Versafire2/HANDFINISHDIR_NEW-shaped machines (whose real
 * activities are filed under a different machine's code) stay mappable.
 *
 * Reordering uses explicit up/down controls and a radio for Run rather than
 * drag-and-drop — per the module update spec, order and the Run choice must
 * read as deliberate decisions, not an implication of list position.
 */
function MapResourceActivityDialog({
  candidate,
  mapping,
  nameCollides,
  onOpenChange,
  onMapped,
}: {
  candidate: API.ResourceActivityCandidate;
  mapping: API.ResourceActivityMapping | null;
  /** Whether another currently-loaded machine shares this one's display
   * name — see `machineLabel`'s docstring. */
  nameCollides: boolean;
  onOpenChange: (open: boolean) => void;
  onMapped: (result: API.ResourceActivityMapping) => void;
}): JSX.Element {
  const adapter = useDataAdapter();
  const [name, setName] = useState(mapping?.name ?? "");
  const [members, setMembers] = useState<PickedMember[]>(
    () =>
      mapping?.members.map((m) => ({
        machine_and_labour_id: m.machine_and_labour_id,
        code: m.code,
        description: m.description,
        is_run: m.is_run,
      })) ?? [],
  );
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<API.ActivityCatalogueEntry[]>([]);
  const [resultsPhase, setResultsPhase] = useState<
    "loading" | "ready" | "error"
  >("loading");
  // An empty scoped search used to just say "No matching activities" — a
  // dead end that reads as "this machine has no activities available" even
  // though the full catalogue (1,400+ entries) is right there. Typing
  // already escapes the scope (see runSearch below); this lets the empty
  // state's own "browse the full catalogue" action do the same without
  // requiring the admin to already know that trick.
  const [browsingFullCatalogue, setBrowsingFullCatalogue] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [membersError, setMembersError] = useState<string | null>(null);
  const [memberErrors, setMemberErrors] = useState<Record<number, string>>({});
  const fetchSeq = useRef(0);

  const runSearch = useCallback(
    async (q: string) => {
      const seq = ++fetchSeq.current;
      setResultsPhase("loading");
      try {
        const result = await adapter.searchActivityCatalogue(
          q
            ? { q }
            : browsingFullCatalogue
              ? {}
              : { resourceCode: candidate.resource_code },
        );
        if (seq === fetchSeq.current) {
          setResults(result.items);
          setResultsPhase("ready");
        }
      } catch {
        if (seq === fetchSeq.current) {
          setResults([]);
          setResultsPhase("error");
        }
      }
    },
    [adapter, candidate.resource_code, browsingFullCatalogue],
  );

  // Loads immediately on open (query is "") and debounces subsequent typed
  // searches — mirrors EntityCombobox's own debounce, see that component.
  useEffect(() => {
    const handle = setTimeout(
      () => void runSearch(query.trim()),
      query.trim() ? 200 : 0,
    );
    return () => clearTimeout(handle);
  }, [query, runSearch]);

  const pickedIds = new Set(members.map((m) => m.machine_and_labour_id));

  function handleAdd(entry: API.ActivityCatalogueEntry): void {
    if (pickedIds.has(entry.machine_and_labour_id)) return;
    setMembers((prev) => [
      ...prev,
      {
        machine_and_labour_id: entry.machine_and_labour_id,
        code: entry.code,
        description: entry.description,
        // The first activity picked defaults to Run — the common case is a
        // single-activity mapping. Later picks stay non-Run until the admin
        // explicitly says otherwise.
        is_run: prev.length === 0,
      },
    ]);
  }

  function handleRemove(index: number): void {
    setMembers((prev) => prev.filter((_, i) => i !== index));
  }

  function handleMove(index: number, direction: -1 | 1): void {
    setMembers((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  function handleSetRun(index: number): void {
    setMembers((prev) => prev.map((m, i) => ({ ...m, is_run: i === index })));
  }

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setNameError(null);
    setMembersError(null);
    setMemberErrors({});
    setSubmitting(true);
    try {
      const memberInputs = members.map(({ machine_and_labour_id, is_run }) => ({
        machine_and_labour_id,
        is_run,
      }));
      const result = mapping
        ? await adapter.editResourceActivityMapping(mapping.id, {
            name,
            members: memberInputs,
          })
        : await adapter.mapResourceActivity(candidate.tharstern_resource_id, {
            resource_code: candidate.resource_code,
            name,
            members: memberInputs,
          });
      onMapped(result);
    } catch (err) {
      // Field-tagged validation (.claude/rules/api-validation.md): an empty
      // or duplicate name binds to `name`; an unknown activity id binds to
      // that row (`members.{i}.machine_and_labour_id`); an empty list or a
      // missing/ambiguous Run binds to the ordered-list section as a whole
      // (`members`).
      const fieldErrors = extractFieldErrors(err);
      if (fieldErrors.length > 0) {
        const nextMemberErrors: Record<number, string> = {};
        let nextMembersError: string | null = null;
        let nextNameError: string | null = null;
        for (const fe of fieldErrors) {
          const match = /^members\.(\d+)\.machine_and_labour_id$/.exec(
            fe.field,
          );
          if (match) {
            nextMemberErrors[Number(match[1])] = fe.message;
          } else if (fe.field === "members") {
            nextMembersError = fe.message;
          } else if (fe.field === "name") {
            nextNameError = fe.message;
          }
        }
        setMemberErrors(nextMemberErrors);
        setMembersError(nextMembersError);
        setNameError(nextNameError);
      } else {
        setError(errorMessage(err, "Could not save this mapping."));
      }
    } finally {
      setSubmitting(false);
    }
  }

  const runCount = members.filter((m) => m.is_run).length;
  const runIndex = members.findIndex((m) => m.is_run);

  return (
    <Dialog open onOpenChange={onOpenChange}>
      {/* Bounded against the viewport with the body as the sole scroll
       * owner (header and footer stay `shrink-0`, pinned outside it) —
       * same shape as JobDetailModal's split. Without this, three
       * stacked growing sections (name, activity search, the ordered
       * member list) push the dialog past the viewport with nothing to
       * scroll it: the PR #1507 job-view clipping root cause, here with
       * Save instead of "Add an alternative mapping" as the control that
       * goes unreachable. */}
      <DialogContent className="flex max-h-[85vh] flex-col overflow-hidden sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {mapping
              ? `Edit "${mapping.name}" for "${machineLabel(candidate, nameCollides)}"`
              : candidate.mappings.length > 0
                ? `Add an alternative for "${machineLabel(candidate, nameCollides)}"`
                : `Map "${machineLabel(candidate, nameCollides)}"`}
          </DialogTitle>
          <DialogDescription>
            Name this mapping, pick one or more activities in order, and mark
            exactly one as the Run. Search defaults to activities filed under{" "}
            <span className="font-medium">{candidate.resource_code}</span> —
            type to search the full Tharstern catalogue, since some machines'
            real activities are filed under a different machine's code.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col gap-4"
        >
          {/* The activity search results already scroll independently
           * inside CommandList's own fixed max-h-[300px] box (a stable
           * search-widget convention used elsewhere in the app,
           * unchanged here). The ordered member list has no such natural
           * cap and is the section real usage grows unboundedly, so it
           * scrolls as part of this outer body instead of getting a
           * second independent scrollbox — nesting two growing,
           * independently-scrolling regions inside one already-scrolling
           * parent trades one bug for a worse one (double-scroll capture
           * fights on trackpad/touch), and the member list is the content
           * the admin is actively reordering, so revealing it by
           * scrolling the body is the more direct interaction. */}
          <div
            className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1"
            data-testid="map-dialog-scroll-body"
          >
            <div className="space-y-1.5">
              <Label htmlFor="mapping-name">Name</Label>
              <Input
                id="mapping-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. 2:1, MR-RUN, Run"
                aria-invalid={!!nameError}
                aria-describedby={nameError ? "mapping-name-error" : undefined}
              />
              {nameError && (
                <p
                  id="mapping-name-error"
                  role="alert"
                  className="text-sm text-destructive"
                >
                  {nameError}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              {/* cmdk's own Command.Input overwrites any `id` prop with its
               * internally-generated one (see the cmdk source), so a
               * `htmlFor`-associated <Label> silently fails to bind. Label via
               * `aria-label` directly on CommandInput instead — this Label is
               * purely the visual heading above the picker. */}
              <Label>Add an activity</Label>
              <div className="rounded-md border">
                <Command shouldFilter={false}>
                  <CommandInput
                    aria-label="Add an activity"
                    placeholder="Search by code or description…"
                    value={query}
                    onValueChange={setQuery}
                  />
                  <CommandList>
                    {resultsPhase === "loading" && (
                      <div className="px-3 py-2 text-xs text-muted-foreground">
                        Loading…
                      </div>
                    )}
                    {resultsPhase === "error" && (
                      <div className="px-3 py-2 text-xs text-destructive">
                        Could not load activities. Try again.
                      </div>
                    )}
                    {resultsPhase === "ready" && results.length === 0 && (
                      <CommandEmpty>
                        <div className="space-y-2 text-center">
                          <p>
                            {query.trim()
                              ? "No activities match that search."
                              : `No activities are filed under ${candidate.resource_code}.`}
                          </p>
                          {!query.trim() && !browsingFullCatalogue && (
                            <Button
                              type="button"
                              variant="link"
                              size="sm"
                              className="h-auto p-0"
                              onClick={() => setBrowsingFullCatalogue(true)}
                            >
                              Browse the full Tharstern catalogue instead
                            </Button>
                          )}
                        </div>
                      </CommandEmpty>
                    )}
                    {resultsPhase === "ready" && results.length > 0 && (
                      <CommandGroup
                        heading={
                          query.trim() || browsingFullCatalogue
                            ? "Full catalogue"
                            : `Scoped to ${candidate.resource_code}`
                        }
                      >
                        {results.map((entry) => {
                          const picked = pickedIds.has(
                            entry.machine_and_labour_id,
                          );
                          return (
                            <CommandItem
                              key={entry.machine_and_labour_id}
                              value={String(entry.machine_and_labour_id)}
                              disabled={picked}
                              onSelect={() => handleAdd(entry)}
                            >
                              <div className="flex min-w-0 flex-col">
                                <span className="truncate">{entry.code}</span>
                                <span className="truncate text-[11px] text-muted-foreground">
                                  {entry.description}
                                  {entry.resource &&
                                  entry.resource !== candidate.resource_code
                                    ? ` · filed under ${entry.resource}`
                                    : ""}
                                </span>
                              </div>
                              {picked && (
                                <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                                  Added
                                </span>
                              )}
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    )}
                  </CommandList>
                </Command>
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>Mapping order — exactly one Run</Label>
                {members.length > 0 && runCount !== 1 && (
                  <span className="text-xs text-muted-foreground">
                    {runCount === 0
                      ? "Pick a Run activity"
                      : "Only one Run allowed"}
                  </span>
                )}
              </div>
              {members.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No activities picked yet.
                </p>
              ) : (
                <RadioGroup
                  value={String(runIndex)}
                  onValueChange={(v) => handleSetRun(Number(v))}
                  className="space-y-2"
                >
                  {members.map((member, index) => (
                    <div
                      key={member.machine_and_labour_id}
                      className="flex items-center gap-2 rounded-md border p-2"
                      data-testid={`mapping-member-${member.machine_and_labour_id}`}
                    >
                      <span className="w-5 shrink-0 text-xs tabular-nums text-muted-foreground">
                        {index + 1}.
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm">{member.code}</div>
                        <div className="truncate text-[11px] text-muted-foreground">
                          {member.description}
                        </div>
                        {memberErrors[index] && (
                          <p role="alert" className="text-xs text-destructive">
                            {memberErrors[index]}
                          </p>
                        )}
                      </div>
                      <Label
                        htmlFor={`mapping-run-${index}`}
                        className="flex shrink-0 items-center gap-1.5 text-xs font-normal"
                      >
                        <RadioGroupItem
                          value={String(index)}
                          id={`mapping-run-${index}`}
                          aria-label={`Mark ${member.code} as Run`}
                        />
                        Run
                      </Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMove(index, -1)}
                        disabled={index === 0}
                        aria-label={`Move ${member.code} earlier`}
                      >
                        <ArrowUp className="size-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMove(index, 1)}
                        disabled={index === members.length - 1}
                        aria-label={`Move ${member.code} later`}
                      >
                        <ArrowDown className="size-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemove(index)}
                        aria-label={`Remove ${member.code}`}
                      >
                        <X className="size-4" />
                      </Button>
                    </div>
                  ))}
                </RadioGroup>
              )}
              {membersError && (
                <p role="alert" className="text-sm text-destructive">
                  {membersError}
                </p>
              )}
            </div>
          </div>

          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || members.length === 0}>
              {submitting ? "Saving…" : "Save mapping"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return "Never";
  return formatDateTimeOrDash(iso);
}
